import { Component } from 'react'
import { bool, func, shape, string, instanceOf } from 'prop-types'
import cn from 'classnames'

import Banner from '../../theme-components/banner.js'
import LoadMoreButton from '../../theme-components/load-more-button.js'
import loadWebFont from '../../../../utilities/loadWebFont.js'
import Settings from '../../../../../models/settings.js'
import GridFeedContent from './grid-feed-content.js'

import getColumnsAndPostWidth from './helper-functions/getColumnsAndPostWidth.js'
import getNumberOfPostsToRender from './helper-functions/getNumberOfPostsToRender.js'
import getRenderedPostsCount from './helper-functions/getRenderedPostsCount.js'
import decideShouldRenderMore from './helper-functions/decideShouldRenderMore.js'

import { VisiblePostsContext } from '../../../../contexts/visiblePostsContext.js'

import '../../../../../styles/themes/grid/grid.scss'
import InfiniteScrollWaypoint from '../../theme-components/infinitescroll-waypoint.js'

export default class GridTheme extends Component {
  static propTypes = {
    containerElement: shape().isRequired,
    feed: shape().isRequired,
    feedKey: string.isRequired,
    load: func.isRequired,
    settings: instanceOf(Settings).isRequired,
    shouldFetchMore: bool.isRequired,
  }

  // eslint-disable-next-line react/sort-comp
  static contextType = VisiblePostsContext

  constructor(props) {
    super(props)

    const containerOffsetWidth = props.containerElement?.offsetWidth

    this.state = {
      containerWidth: !containerOffsetWidth ? props.settings.containerWidth : containerOffsetWidth,
      observer: new IntersectionObserver(
        (entries) => {
          const { addVisiblePostId, removeVisiblePostId } = this.context
          entries.forEach((entry) => {
            const { postKey } = entry.target.dataset
            if (entry.isIntersecting) {
              addVisiblePostId(postKey)
            } else {
              removeVisiblePostId(postKey)
            }
          })
        },
        { threshold: 0.2 },
      ),
      timesLoaded: 1,
    }

    this.handleResize = () => {
      const offsetWidth = this.props.containerElement?.offsetWidth

      this.setState({
        containerWidth: !offsetWidth ? this.props.settings.containerWidth : offsetWidth,
      })
    }
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleResize, { passive: true })
    loadWebFont(this.props.settings)
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize)
    this.state.observer.disconnect()
  }

  loadMore = () => {
    this.setState((prevState) => ({
      timesLoaded: prevState.timesLoaded + 1,
    }))
  }

  render() {
    const { feed } = this.props
    const hasPosts = feed?.posts?.length !== 0

    if (!hasPosts) {
      return null
    }

    // We filter out posts with no media in this theme
    const filteredPosts = feed.posts.filter((post) => post.hasMedia || post.isYoutubePost)

    if (!filteredPosts.length) {
      return null
    }

    const { settings, feedKey, load, shouldFetchMore, containerElement } = this.props
    const { containerWidth, timesLoaded } = this.state

    const { columns, postWidth } = getColumnsAndPostWidth(settings, containerWidth)

    const {
      containerFixedHeight,
      postCount,
      fontFamily,
      feedFontSize: fontSize,
      feedBackgroundColor,
      showTopBanner,
      showBottomBanner,
      theme,
    } = settings

    const postsToRenderCount = getNumberOfPostsToRender(
      filteredPosts.length,
      columns,
      timesLoaded,
      settings,
    )

    const shouldRenderMore = decideShouldRenderMore(
      postsToRenderCount,
      filteredPosts.length,
      postCount,
      columns,
      shouldFetchMore,
    )

    const feedStyles = {
      fontFamily,
      fontSize,
      backgroundColor: feedBackgroundColor,
    }

    return (
      <div className="bzfy-t-feed-tile-grid" data-testid={theme} style={feedStyles}>
        <div
          className={cn('bzfy-feed', {
            'bzfy-feed--fixed': !!containerFixedHeight,
            'bzfy-feed--has-banner-top': showTopBanner,
          })}
          style={{ height: containerFixedHeight || 'auto' }}
        >
          {showTopBanner && hasPosts ? <Banner settings={settings} /> : null}
          <div className="bzfy-o-container" style={{ maxWidth: containerWidth }}>
            <GridFeedContent
              columns={columns}
              containerElement={containerElement}
              containerWidth={containerWidth}
              feed={feed}
              feedKey={feedKey}
              maxPosts={getRenderedPostsCount(columns, settings, postsToRenderCount)}
              observer={this.state.observer}
              postWidth={postWidth}
              settings={settings}
            />
          </div>
          {showBottomBanner && hasPosts ? <Banner settings={settings} /> : null}
          {shouldRenderMore ? (
            <LoadMoreButton
              backgroundColor={feedStyles.backgroundColor}
              currentPostsCount={postsToRenderCount}
              feedKey={feedKey}
              load={load}
              loadMore={this.loadMore}
              settings={settings}
            />
          ) : null}
          {settings.theme !== 'tile-grid-auto' && shouldRenderMore ? (
            <InfiniteScrollWaypoint
              load={this.props.load}
              posts={this.props.feed.posts}
              settings={settings}
            />
          ) : null}
        </div>
      </div>
    )
  }
}
